import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { List, Record } from 'immutable';
import classNames from 'classnames';
import useSports from '@xanadu/ui-shared/hooks/useSports';

import { MOBILE_BREAKPOINT, PRODUCTS, PRODUCT_LINKS, PRODUCT_ROUTES, RACING_SPORTS } from '../../../constants';

import Search from '../../../containers/header/Search';
import MBZeroSportTiles from '../../../containers/navigation/MBZeroSportTiles';

import Tile from './Tile';

import styles from './MobileNavigation.module.scss';

const MobileNavigation = ({
  tags,
  topFive,
  navigationVisible,
  history,
  toggleNavigation,
  widthSize,
  changeProduct,
  product,
  casinoVisibilityRestricted,
  virtualsAllowed,
  inPlayTags,
}) => {
  const [searchFocused, setSearchFocus] = useState(false);

  const isExchange = product === PRODUCTS.exchange;
  const isMBZero = product === PRODUCTS.mbzero;
  const isMobile = widthSize <= MOBILE_BREAKPOINT;

  const productUrlPrefix = PRODUCT_ROUTES[product];

  const allSports = useSports(tags, isExchange);

  const navigate = useCallback(({ url, product: productUpdate }) => {
    toggleNavigation(false);

    const timer = setTimeout(() => {
      if (productUpdate) {
        changeProduct(productUpdate);
      }

      history.push(url);
    }, 270);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const onSearchFocusChange = (searchFocus) => {
    setSearchFocus(searchFocus);
  };

  let topFiveSports = [];

  if (tags.size) {
    topFiveSports = topFive.slice(0, 3).reduce((acc, item) => {
      const existingTag = tags.find(t => t.id === item.id);
      const { children = [], url, urlName } = existingTag || new Record();
      const { url: firstSubMetaTagUrlName } = tags.find(t => t.uuid === children[0]) || {};

      const setFirstCompetitionUrl = !RACING_SPORTS.includes(urlName) && children.length === 1;

      if (existingTag) {
        return [
          ...acc,
          existingTag.set('url', setFirstCompetitionUrl ? firstSubMetaTagUrlName : url)
        ];
      }

      return acc;
    }, []);

    if (inPlayTags.size) {
      topFiveSports.push({
        urlName: 'live-betting',
        id: 'live-betting',
        name: 'Live Betting',
        url: '/live-betting',
      });
    }
  }

  const products = useMemo(() => {
    const filteredProducts = PRODUCT_LINKS.filter(p => {
      if (p.id === PRODUCTS.virtuals) {
        return virtualsAllowed && product !== PRODUCTS.virtuals;
      }

      if (casinoVisibilityRestricted) {
        return ![PRODUCTS.casino, PRODUCTS.liveCasino].includes(p.id);
      }

      if (isMobile) {
        return p.id !== product && ![PRODUCTS.mbzero].includes(p.id);
      }

      return p.id !== product;
    });

    return filteredProducts.map(({
      id,
      name,
      urlName,
      url
    }) => {
      const isPredictions = id === PRODUCTS.predictions;
      const isLinkExternal = url.startsWith('http');
      return (
        <Tile
          key={id}
          product={id}
          name={name}
          iconName={urlName || PRODUCTS.exchange}
          url={url}
          onClick={!isPredictions ? navigate : undefined}
          dataHook={`mob-nav-${id}`}
          className=".gtm_cta"
          showLabel
          isExternalLink={isLinkExternal}
          openInNewTab={!(isPredictions && isLinkExternal)}
        />
      );
    });
  }, [product, virtualsAllowed, casinoVisibilityRestricted, isMobile]);

  useEffect(() => {
    document.documentElement.style.overflow = navigationVisible ? 'hidden' : '';

    return () => {
      document.documentElement.style.overflow = '';
    };
  }, [navigationVisible]);

  const mainClass = classNames({
    [styles.main]: true,
    [styles['main--opened']]: navigationVisible
  });

  const startAligedSectionClass = classNames({
    [styles.sectionContent]: true,
    [styles.rowStart]: true
  });

  if (widthSize > MOBILE_BREAKPOINT) {
    return null;
  }

  return (
    <div className={mainClass}>
      {navigationVisible && (
        <Fragment>
          {isExchange && (
            <Search
              onInputFocusChange={onSearchFocusChange}
              onNavigate={navigate}
              product={product}
            />
          )}
          {!searchFocused && (
            <Fragment>
              {isExchange && (
                <div>
                  <div className={styles.sectionHeader}>Popular Exchange Sports</div>
                  <div className={styles.sectionContent}>
                    {topFiveSports.map(({ urlName, id, name, url }) => (
                      <Tile
                        key={id}
                        name={name}
                        iconName={urlName}
                        url={`${productUrlPrefix}/events${url}`}
                        onClick={navigate}
                        showLabel
                      />
                    ))}
                  </div>
                </div>
              )}
              {isMBZero && (
                <div>
                  <div className={styles.sectionHeader}>MBZero Sports</div>
                  <MBZeroSportTiles navigateFunction={navigate} />
                </div>
              )}
              <div>
                <div className={styles.sectionHeader}>More from Matchbook</div>
                <div className={startAligedSectionClass}>
                  {products}
                </div>
              </div>
              {isExchange && (
                <div>
                  <div className={styles.sectionHeader}>All Sports</div>
                  <div className={styles.sectionContent}>
                    {allSports.map(({ id, urlName, url, name }) => (
                      <Tile
                        key={id}
                        name={name}
                        iconName={urlName}
                        url={urlName === 'colossus' ? '/colossus' : `${productUrlPrefix}/events${url}`}
                        onClick={navigate}
                        showLabel
                      />
                    ))}
                  </div>
                </div>
              )}
            </Fragment>
          )}
        </Fragment>
      )}
    </div>
  );
};

MobileNavigation.propTypes = {
  navigationVisible: PropTypes.bool.isRequired,
  widthSize: PropTypes.number.isRequired,
  toggleNavigation: PropTypes.func.isRequired,
  changeProduct: PropTypes.func.isRequired,
  topFive: PropTypes.instanceOf(List).isRequired,
  tags: PropTypes.instanceOf(List).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }),
  product: PropTypes.string.isRequired,
  virtualsAllowed: PropTypes.bool.isRequired,
  casinoVisibilityRestricted: PropTypes.bool.isRequired,
  inPlayTags: PropTypes.instanceOf(List).isRequired,
};

export default MobileNavigation;

